import { Children, cloneElement, isValidElement, type HTMLAttributes, type ReactNode, type Ref } from 'react'
import { cn } from './cn'

type AnyProps = Record<string, unknown>

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node)
      else if (ref && typeof ref === 'object') (ref as { current: T | null }).current = node
    }
  }
}

export interface SlotProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode
  ref?: Ref<HTMLElement>
}

/**
 * Renders its only child, merging className, style, event handlers and refs onto it.
 * Powers the `asChild` prop (for example `<Button asChild><Link to="/x">Go</Link></Button>`).
 */
export function Slot({ children, ref, ...slotProps }: SlotProps) {
  const child = Children.only(children)
  if (!isValidElement(child)) return null
  const childProps = child.props as AnyProps
  const merged: AnyProps = { ...slotProps, ...childProps }
  for (const key of Object.keys(slotProps)) {
    const slotValue = (slotProps as AnyProps)[key]
    const childValue = childProps[key]
    if (/^on[A-Z]/.test(key) && typeof slotValue === 'function' && typeof childValue === 'function') {
      merged[key] = (...args: unknown[]) => {
        ;(childValue as (...a: unknown[]) => void)(...args)
        ;(slotValue as (...a: unknown[]) => void)(...args)
      }
    }
  }
  merged.className = cn(slotProps.className, childProps.className as string | undefined)
  if (slotProps.style || childProps.style) {
    merged.style = { ...slotProps.style, ...(childProps.style as object | undefined) }
  }
  const childRef = childProps.ref as Ref<HTMLElement> | undefined
  if (ref || childRef) merged.ref = mergeRefs(ref, childRef)
  return cloneElement(child, merged)
}
